import React, { Fragment } from 'react';
import { Form, Input, Button, Divider, notification, Typography, Space, Checkbox, Select } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import CountrySelect from '../../countries/components/CountrySelect';
import CustomerService from '../services/';
import api from '../../../services/api/';
import AuthContext from '../../../context/authProvider';

class CustomerShippingForm extends React.Component {
    static contextType = AuthContext;

    constructor(props) {
        super(props);
        this.state = {
            customer: props.customer,
            shipping: props.shipping || {
                first_name: '',
                last_name: '',
                contact_number: '',
                contact_country_id: null,
                email: '',
                use_shipping_email: false,
                address_line_1: '',
                address_line_2: '',
                postal_code: '',
                country_id: null,
                state_id: null,
                is_default: true
            },
            countries: [],
            loadingCountries: false,
            countryCode: '1',
            countryId: null,
            nationalNumber: '',
            defaultCountryCode: '1',
            defaultCountryId: null,
            organization: null
        };
    }

    componentDidMount() {
        this.initializeOrganization();
        this.fetchCountries();
    }

    initializeOrganization = () => {
        // Get organization from AuthContext (similar to useAuthSettingUtils().getAuthOrganization())
        if (!this.context || !this.context.auth) {
            // Fallback to defaults if no auth context
            this.setState(prevState => ({
                defaultCountryCode: '1',
                defaultCountryId: null,
                countryCode: '1',
                countryId: null,
                shipping: {
                    ...prevState.shipping,
                    country_id: prevState.shipping.country_id || null,
                    state_id: prevState.shipping.state_id || null,
                }
            }), () => {
                if (this.state.countries.length > 0) {
                    this.initializeContactNumber();
                }
            });
            return;
        }

        const organization = this.context.auth.organization;
        if (organization) {
            const defaultCountryCode = organization?.country?.phone_code || '1';
            const defaultCountryId = organization?.country?.id || null;
            this.setState(prevState => ({
                organization: organization,
                defaultCountryCode: defaultCountryCode,
                defaultCountryId: defaultCountryId,
                countryCode: prevState.countryId ? prevState.countryCode : defaultCountryCode,
                countryId: prevState.countryId || defaultCountryId,
                shipping: {
                    ...prevState.shipping,
                    country_id: prevState.shipping.country_id || organization?.country?.id || null,
                    state_id: prevState.shipping.state_id || organization?.state?.id || null,
                }
            }), () => {
                if (this.state.countries.length > 0) {
                    this.initializeContactNumber();
                }
            });
        } else {
            // Fallback to defaults
            this.setState(prevState => ({
                defaultCountryCode: '1',
                defaultCountryId: null,
                countryCode: '1',
                countryId: null,
                shipping: {
                    ...prevState.shipping,
                    country_id: prevState.shipping.country_id || null,
                    state_id: prevState.shipping.state_id || null,
                }
            }), () => {
                if (this.state.countries.length > 0) {
                    this.initializeContactNumber();
                }
            });
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.context && this.context.auth && this.context.auth.organization) {
            const organization = this.context.auth.organization;
            if (prevState.organization?.id !== organization?.id) {
                this.initializeOrganization();
            }
        }

        // Update state when props change (e.g., when customer data is loaded)
        if (prevProps.shipping !== this.props.shipping && this.props.shipping) {
            this.setState({
                shipping: this.props.shipping
            }, () => {
                // Initialize contact number after state is updated and countries are loaded
                if (this.state.countries.length > 0) {
                    this.initializeContactNumber();
                }
            });
        }
        if (prevProps.customer !== this.props.customer && this.props.customer) {
            this.setState({
                customer: this.props.customer
            });
        }
    }

    fetchCountries = async () => {
        this.setState({ loadingCountries: true });
        try {
            const response = await api.get('/countries');
            const countriesData = response.data.data || [];
            const sortedCountries = countriesData.sort((a, b) => a.name.localeCompare(b.name));
            this.setState({ countries: sortedCountries }, () => {
                // Initialize contact number after countries are loaded
                this.initializeContactNumber();
            });
        } catch (error) {
            console.error('Error fetching countries:', error);
        } finally {
            this.setState({ loadingCountries: false });
        }
    }

    parseContactNumber = (contactNumber) => {
        if (!contactNumber) {
            return { countryCode: this.state.defaultCountryCode, nationalNumber: '' };
        }
        
        let cleaned = contactNumber.toString().trim();
        
        if (cleaned.startsWith('+')) {
            cleaned = cleaned.substring(1);
            const digits = cleaned.replace(/\D/g, '');
            
            if (!digits) {
                return { countryCode: this.state.defaultCountryCode, nationalNumber: '' };
            }
            
            const phoneCodes = this.state.countries.length > 0 
                ? [...new Set(this.state.countries.map(c => c.phone_code))].sort((a, b) => b.length - a.length)
                : [this.state.defaultCountryCode];
            
            let countryCode = this.state.defaultCountryCode;
            let nationalNumber = digits;
            
            // Try to match country codes
            for (const code of phoneCodes) {
                if (digits.startsWith(code)) {
                    const remaining = digits.substring(code.length);
                    if (remaining.length >= 4 && remaining.length <= 15) {
                        countryCode = code;
                        nationalNumber = remaining;
                        break;
                    }
                }
            }
            
            if (nationalNumber === digits && digits.startsWith(this.state.defaultCountryCode)) {
                const remaining = digits.substring(this.state.defaultCountryCode.length);
                if (remaining.length >= 4) {
                    countryCode = this.state.defaultCountryCode;
                    nationalNumber = remaining;
                }
            }
            
            return { countryCode, nationalNumber };
        }
        
        const digits = cleaned.replace(/\D/g, '');
        return { countryCode: this.state.defaultCountryCode, nationalNumber: digits };
    }

    combineContactNumber = (countryCode, nationalNumber) => {
        const code = (countryCode || this.state.defaultCountryCode).replace(/\D/g, '');
        const number = (nationalNumber || '').replace(/\D/g, '');
        if (!code && !number) return '';
        return `+${code}${number}`;
    }

    getPhoneCodeFromCountryId = (countryId) => {
        if (!countryId) return this.state.defaultCountryCode;
        const country = this.state.countries.find(c => String(c.id) === String(countryId));
        return country ? String(country.phone_code) : this.state.defaultCountryCode;
    }

    initializeContactNumber = () => {
        const shipping = this.props.shipping || this.state.shipping;
        if (!shipping) return;

        // Default to organization's country if no contact_country_id exists
        let countryId = shipping.contact_country_id || null;
        if (!countryId) {
            countryId = this.state.defaultCountryId || null;
        }

        let phoneCode = shipping.extra?.contact_country_phone_code || null;
        if (phoneCode && typeof phoneCode === 'string') {
            phoneCode = phoneCode.replace(/^\+/, '');
        }

        // Wait for countries to load before trying to get phone code
        if (!phoneCode && this.state.countries.length > 0 && countryId) {
            phoneCode = this.getPhoneCodeFromCountryId(countryId);
        } else if (!phoneCode) {
            // Default to organization's phone code
            phoneCode = this.state.defaultCountryCode || '1';
        }
        phoneCode = String(phoneCode);

        let nationalNumber = '';
        if (shipping.contact_number) {
            let contactNumberStr = shipping.contact_number.toString().trim();
            if (contactNumberStr.startsWith('+')) {
                contactNumberStr = contactNumberStr.substring(1);
            }
            const digits = contactNumberStr.replace(/\D/g, '');
            if (phoneCode && digits.startsWith(phoneCode)) {
                nationalNumber = digits.substring(phoneCode.length);
            } else {
                const parsed = this.parseContactNumber(shipping.contact_number);
                nationalNumber = parsed.nationalNumber;
            }
        }

        // Ensure countryId is set to organization's default if still null
        if (!countryId && this.state.defaultCountryId) {
            countryId = this.state.defaultCountryId;
        }

        // Update state with parsed values and ensure shipping state has all fields
        this.setState(prevState => ({
            countryCode: phoneCode,
            countryId: countryId || prevState.countryId,
            nationalNumber: nationalNumber,
            shipping: {
                ...prevState.shipping,
                ...shipping, // Preserve all shipping fields from props
                contact_country_id: countryId || prevState.shipping.contact_country_id,
                contact_number: shipping.contact_number || prevState.shipping.contact_number || ''
            }
        }));
    }

    handleShippingChange = (field, value) => {
        this.setState(prevState => ({
            shipping: {
                ...prevState.shipping,
                [field]: value
            }
        }));
    }

    handleContactCountryChange = (countryId) => {
        const country = this.state.countries.find(c => String(c.id) === String(countryId));
        
        if (!country) {
            const code = String(this.state.defaultCountryCode);
            this.setState({
                countryCode: code,
                countryId: this.state.defaultCountryId
            });
            this.handleShippingChange('contact_country_id', this.state.defaultCountryId);
            const combined = this.combineContactNumber(code, this.state.nationalNumber);
            this.handleShippingChange('contact_number', combined);
            return;
        }
        
        const code = String(country.phone_code);
        this.setState({
            countryCode: code,
            countryId: country.id
        });
        
        this.handleShippingChange('contact_country_id', country.id);
        const combined = this.combineContactNumber(code, this.state.nationalNumber);
        this.handleShippingChange('contact_number', combined);
    }

    handleNationalNumberChange = (value) => {
        const nationalNumber = value.replace(/\D/g, '');
        this.setState({ nationalNumber });
        
        const combined = this.combineContactNumber(this.state.countryCode, nationalNumber);
        this.handleShippingChange('contact_number', combined);
    }

    formatPhoneNumber = (value) => {
        if (!value) return value;
        const phoneNumber = value.replace(/\D/g, '');
        const phoneNumberLength = phoneNumber.length;
        
        if (phoneNumberLength < 4) return phoneNumber;
        if (phoneNumberLength < 7) {
            return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
        }
        return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }

    handleCountryChange = (value) => {
        this.setState(prevState => ({
            shipping: {
                ...prevState.shipping,
                country_id: value.country_id,
                state_id: value.state_id
            }
        }));
    }

    getShippingData = () => {
        // Ensure contact_number and contact_country_id are up to date
        const combined = this.combineContactNumber(this.state.countryCode, this.state.nationalNumber);
        
        // Get the original shipping data from props if available (for existing records)
        const originalShipping = this.props.shipping || {};
        
        // Get all current shipping data, ensuring we preserve all fields even if user didn't type
        // Priority: current state > original props > defaults
        const shippingData = {
            // Start with original shipping data from props (preserves existing data including id)
            ...originalShipping,
            // Override with current state (user's changes)
            ...this.state.shipping,
            // Ensure contact fields are properly set
            contact_number: combined || this.state.shipping.contact_number || originalShipping.contact_number || '',
            contact_country_id: this.state.countryId || this.state.shipping.contact_country_id || originalShipping.contact_country_id || this.state.defaultCountryId,
            // Preserve all other fields, using state if available, otherwise original, otherwise defaults
            first_name: this.state.shipping.first_name || originalShipping.first_name || '',
            last_name: this.state.shipping.last_name || originalShipping.last_name || '',
            email: this.state.shipping.email || originalShipping.email || '',
            use_shipping_email: this.state.shipping.use_shipping_email !== undefined 
                ? this.state.shipping.use_shipping_email 
                : (originalShipping.use_shipping_email !== undefined ? originalShipping.use_shipping_email : false),
            address_line_1: this.state.shipping.address_line_1 || originalShipping.address_line_1 || '',
            address_line_2: this.state.shipping.address_line_2 || originalShipping.address_line_2 || '',
            postal_code: this.state.shipping.postal_code || originalShipping.postal_code || '',
            country_id: this.state.shipping.country_id || originalShipping.country_id || null,
            state_id: this.state.shipping.state_id || originalShipping.state_id || null,
            is_default: this.state.shipping.is_default !== undefined
                ? this.state.shipping.is_default 
                : (originalShipping.is_default !== undefined ? originalShipping.is_default : true)
        };
        
        // Preserve id if it exists (for updates)
        if (originalShipping.id) {
            shippingData.id = originalShipping.id;
        }
        
        return shippingData;
    }

    handleShippingUpdate = event => {
        event.preventDefault();
        
        if (!this.state.shipping.first_name) {
            notification.open({
                placement: 'top',
                type: 'error',
                message: 'Cannot submit the form',
                description: 'Please enter the shipping name. This field cannot be left empty.',
            });
            return;
        }

        CustomerService.updateShipping(this.props.idMain, this.state.shipping)
            .then((res) => {
                const result = res.data;
                if (result.status === 200) {
                    notification.open({
                        placement: 'top',
                        type: 'success',
                        message: 'Success',
                        description: 'Shipping information updated successfully',
                    });
                    this.props.customerFormCallBack(result.data);
                }
            })
            .catch((error) => {
                notification.open({
                    placement: 'top',
                    type: 'error',
                    message: 'Error',
                    description: error.data?.message || 'Something went wrong. Please try again.',
                });
            });
    }

    render() {
        const { shipping } = this.state;

        return (
            <Fragment>
                <Form

                    layout="vertical"
                >
                    <Divider titlePlacement="start" plain>Shipping Information</Divider>

                    <Form.Item
                        label="First Name"
                        required
                    >
                        <Input 
                            value={shipping.first_name}
                            onChange={(e) => this.handleShippingChange('first_name', e.target.value)}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Last Name"
                        required
                    >
                        <Input 
                            value={shipping.last_name}
                            onChange={(e) => this.handleShippingChange('last_name', e.target.value)}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Contact Number"
                    >
                        <Space.Compact style={{ display: 'flex', width: '100%' }}>
                            <Select
                                showSearch
                                placeholder="Select country"
                                style={{ width: 200 }}
                                value={this.state.countryId || this.state.defaultCountryId ? String(this.state.countryId || this.state.defaultCountryId) : undefined}
                                onChange={this.handleContactCountryChange}
                                loading={this.state.loadingCountries}
                                listHeight={256}
                                filterOption={(input, option) => {
                                    if (!input) return true;
                                    const searchText = input.toLowerCase();
                                    const country = option?.country;
                                    if (country) {
                                        return (
                                            country.name.toLowerCase().includes(searchText) ||
                                            country.phone_code.includes(searchText) ||
                                            (country.iso2 && country.iso2.toLowerCase().includes(searchText))
                                        );
                                    }
                                    return String(option?.value || '').toLowerCase().includes(searchText);
                                }}
                                getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
                            >
                                {this.state.countries.map((country) => (
                                    <Select.Option 
                                        key={`country-${country.id}`}
                                        value={String(country.id)}
                                        country={country}
                                        label={
                                            <span>
                                                {country.emoji && <span style={{ marginRight: 6 }}>{country.emoji}</span>}
                                                <span>{country.phone_code}</span>
                                            </span>
                                        }
                                    >
                                        <span>
                                            <span style={{ marginRight: 8 }}>+{country.phone_code}</span>
                                            <span>{country.name}</span>
                                        </span>
                                    </Select.Option>
                                ))}
                            </Select>
                            <Input
                                placeholder="Phone number"
                                style={{ flex: 1 }}
                                maxLength={15}
                                value={this.formatPhoneNumber(this.state.nationalNumber)}
                                onChange={(e) => this.handleNationalNumberChange(e.target.value)}
                                onBlur={() => {
                                    const combined = this.combineContactNumber(this.state.countryCode, this.state.nationalNumber);
                                    this.handleShippingChange('contact_number', combined);
                                    if (this.state.countryId) {
                                        this.handleShippingChange('contact_country_id', this.state.countryId);
                                    }
                                }}
                            />
                        </Space.Compact>
                    </Form.Item>

                    <Form.Item
                        label="Email"
                    >
                        <Space orientation="vertical" style={{ width: '100%' }}>
                            <Checkbox
                                checked={!shipping.use_shipping_email}
                                onChange={(e) => this.handleShippingChange('use_shipping_email', !e.target.checked)}
                            >
                                Use primary account email {this.state.customer?.extra?.email}
                            </Checkbox>
                            
                            {shipping.use_shipping_email && (
                                <Input
                                    value={shipping.email}
                                    onChange={(e) => this.handleShippingChange('email', e.target.value)}
                                    placeholder="Enter shipping email address"
                                />
                            )}
                            <Typography.Text type="secondary" className="fs--1">
                                When checked, shipping notifications will be sent to the primary account email. 
                                Uncheck to specify a different email for shipping communications.
                            </Typography.Text>
                        </Space>
                    </Form.Item>

                    <Form.Item
                        label="Location"
                    >
                        <CountrySelect
                            selectedCountry={shipping.country_id}
                            selectedState={shipping.state_id}
                            onChange={this.handleCountryChange}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Address Line 1"
                        required
                    >
                        <Input 
                            value={shipping.address_line_1}
                            onChange={(e) => this.handleShippingChange('address_line_1', e.target.value)}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Address Line 2"
                    >
                        <Input 
                            value={shipping.address_line_2}
                            onChange={(e) => this.handleShippingChange('address_line_2', e.target.value)}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Postal Code"
                        required
                    >
                        <Input 
                            value={shipping.postal_code}
                            onChange={(e) => this.handleShippingChange('postal_code', e.target.value)}
                        />
                    </Form.Item>

                    {/* <Divider plain orientation="left">Save Shipping Information</Divider> */}
                    {/* <Form.Item>
                        <Button 
                            type="primary" 
                            icon={<SaveOutlined />} 
                            onClick={this.handleShippingUpdate}
                        >
                            Save Shipping Information
                        </Button>
                    </Form.Item> */}
                </Form>
            </Fragment>
        );
    }
}

export default CustomerShippingForm; 